import { Injectable, computed, signal } from '@angular/core';
import {
  Counts,
  EmailContract,
  EmailListItemContract,
  EmailsConfig,
  LoadingButtons,
  TransformEmailListItemContract,
} from '@app-types/api/email';
import { SearchInfo, SearchParams } from '@app-types/search';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { EmailSearchOrder } from '@app-types/enums/email.search-order';
import { OrderDirection } from '@app-types/enums/order-direction';
import { OptionsEnum } from '@app-types/common';
import { MailFolderType } from '@app-types/enums/mail-folder.type';

@Injectable({ providedIn: 'root' })
export class EmailsPageStateService {
  initState: EmailsConfig = {
    isFirstRowTo: false,
    isFirstRowFrom: false,
    hasSourceColumn: false,
    isMailFolderTypeOutbox: false,
    isAllFoldersSelected: false,
    isShowFolderColumn: false,
    styleContainerHoverMenu: '',
    multipleReassignIsAvailable: false,
    isImportEML: false,
    canFinalDelete: false,
    defaultSortOrder: 'date',
    defaultOrderDirection: 'desc',
    selectedMailFolderId: null,
    selectedMailFolderIds: [],
  };
  configPage = signal<EmailsConfig>({ ...this.initState });
  readonly isShowFolderColumn = computed(
    () => this.configPage().isShowFolderColumn
  );
  readonly isImportEML = computed(() => this.configPage().isImportEML);
  readonly isFirstRowTo = computed(() => this.configPage().isFirstRowTo);
  readonly isFirstRowFrom = computed(() => this.configPage().isFirstRowFrom);
  readonly hasSourceColumn = computed(() => this.configPage().hasSourceColumn);
  readonly isMailFolderTypeOutbox = computed(
    () => this.configPage().isMailFolderTypeOutbox
  );
  readonly isAllFoldersSelected = computed(
    () => this.configPage().isAllFoldersSelected
  );
  readonly styleContainerHoverMenu = computed(
    () => this.configPage().styleContainerHoverMenu
  );
  readonly multipleReassignIsAvailable = computed(
    () => this.configPage().multipleReassignIsAvailable
  );
  readonly defaultSortOrder = computed(
    () => this.configPage().defaultSortOrder
  );
  readonly defaultOrderDirection = computed(
    () => this.configPage().defaultOrderDirection
  );
  readonly canFinalDelete = computed(() => this.configPage().canFinalDelete);
  readonly selectedMailFolderId = computed(
    () => this.configPage().selectedMailFolderId
  );
  readonly selectedMailFolderIds = computed(
    () => this.configPage().selectedMailFolderIds
  );

  initStateLoading = {
    isLoadingValidate: false,
    replyingEmailId: null,
    replyingAllEmailId: null,
    forwardingAsTextEmailId: null,
    forwardingAsAttachmentEmailId: null,
    loadingActionFromHeader: false,
    isDownloadingSingleEML: false,
    isDownloadingEML: false,
    isStarred: false,
  };
  loadingButtons = signal<LoadingButtons>({ ...this.initStateLoading });
  readonly replyingEmailId = computed(
    () => this.loadingButtons().replyingEmailId
  );
  readonly replyingAllEmailId = computed(
    () => this.loadingButtons().replyingAllEmailId
  );
  readonly forwardingAsTextEmailId = computed(
    () => this.loadingButtons().forwardingAsTextEmailId
  );
  readonly forwardingAsAttachmentEmailId = computed(
    () => this.loadingButtons().forwardingAsAttachmentEmailId
  );
  readonly loadingActionFromHeader = computed(
    () => this.loadingButtons().loadingActionFromHeader
  );
  readonly isLoadingValidate = computed(
    () => this.loadingButtons().isLoadingValidate
  );
  readonly isLoadingStarred = computed(() => this.loadingButtons().isStarred);
  readonly isDownloadingSingleEML = computed(
    () => this.loadingButtons().isDownloadingSingleEML
  );
  readonly isDownloadingEML = computed(
    () => this.loadingButtons().isDownloadingEML
  );
  readonly isAnyActionInProgress = computed(() => {
    const state = this.loadingButtons();
    return (
      !!state.replyingEmailId ||
      !!state.replyingAllEmailId ||
      !!state.forwardingAsTextEmailId ||
      !!state.forwardingAsAttachmentEmailId
    );
  });

  public infoOpenedMailAccount: SearchInfo;
  public displayedColumns: string[] = [];
  public dataSource = new MatTableDataSource<TransformEmailListItemContract>(
    []
  );
  public selection = new SelectionModel<number>(true, []);
  public counts: Counts;
  public searchOrder: EmailSearchOrder;
  public searchParams: SearchParams = new SearchParams();
  public orderDirection: OrderDirection;
  onlyUnseen: boolean | null = null;
  public searchWidgetId: number;

  isReloadLoading = false;
  public showLoader = true;
  public isError = false;
  public searchOrders: OptionsEnum[];

  public updatedEmail: EmailContract | EmailListItemContract | null;
  public infoInitialContactsTo: {
    mailAccountId: number;
    to: string[];
  } | null = null;
  public auditMailId: number;
  public metaMailId: number;
  emailIdsForGroup: number[] | null = null;
  emailsForTags: {
    emails: EmailListItemContract[];
    ids: number[];
    mailAccountId?: number;
  } | null = null;
  public openedEmail: EmailListItemContract | null;
  public notificationMailId: number;
  public notesMailId: number;
  successDeleteEmailIds: number[] = [];

  setDataTable(data: TransformEmailListItemContract[]): void {
    this.dataSource.data = data;
  }

  addDataTable(data: TransformEmailListItemContract[]): void {
    this.dataSource.data = [...this.dataSource.data, ...data];
  }

  changeDataTable(ids: number[], key: string, value: any): void {
    const idSet = new Set(ids);
    this.dataSource.data = this.dataSource.data.map(e =>
      idSet.has(e.emailId) ? { ...e, [key]: value } : e
    );
  }

  changeObjectDataTable(
    ids: number[],
    value: Partial<TransformEmailListItemContract>
  ): void {
    const idSet = new Set(ids);
    this.dataSource.data = this.dataSource.data.map(e =>
      idSet.has(e.emailId) ? { ...e, ...value } : e
    );
  }

  filterDataTable(ids: number[]): void {
    const idSet = new Set(ids);
    this.dataSource.data = this.dataSource.data.filter(
      e => !idSet.has(e.emailId)
    );
  }

  getEmails(ids: number[]): TransformEmailListItemContract[] {
    const idSet = new Set(ids);
    return this.dataSource.data.filter(e => idSet.has(e.emailId));
  }

  updateConfigPage(data: Partial<EmailsConfig>): void {
    this.configPage.update(prev => ({
      ...prev,
      ...data,
    }));
  }

  resetConfigPage(): void {
    this.configPage.set({ ...this.initState });
  }

  resetLoadingButtons(): void {
    this.loadingButtons.set({ ...this.initStateLoading });
  }

  updateLoadingButtons(data: Partial<LoadingButtons>): void {
    this.loadingButtons.update(prev => ({
      ...prev,
      ...data,
    }));
  }

  updateCounts(data: Partial<Counts>): void {
    this.counts = {
      ...this.counts,
      ...data,
    };
  }

  resetData(): void {
    this.resetConfigPage();
    this.resetLoadingButtons();
    this.displayedColumns = [];
    this.dataSource = new MatTableDataSource<TransformEmailListItemContract>(
      []
    );
    this.selection = new SelectionModel<number>(true, []);
    this.updateCounts({
      ...(this.infoOpenedMailAccount.type === MailFolderType.Inbox
        ? this.counts
        : {}),
      totalCount: 0,
    });
    this.searchParams = new SearchParams();
    this.onlyUnseen = null;
    this.isReloadLoading = false;
    this.showLoader = true;
    this.isError = false;
    this.updatedEmail = null;
    this.emailIdsForGroup = null;
    this.emailsForTags = null;
    this.openedEmail = null;
    this.successDeleteEmailIds = [];
  }
}
